// The PUBLIC album viewer (/a/<token>[/<slug>]) — a standalone, read-only share page
// (#302/#304). A visitor has NO login, NO Session, and NO owner chrome: the App renders
// this view for every /a/… path BEFORE any auth / /api/me logic (see App.tsx), so a public
// visitor never triggers a `/api/me` call or the join wall.
//
// It fetches the album's PUBLIC manifest with a plain, same-origin, credential-less `fetch`
// (NOT the Session-gated api client) and then renders the SAME <MediaGrid> the owner gallery
// uses — so Select (ZIP), Mosaic (ordered pick), Contact sheet, the lightbox, and per-item
// download behave IDENTICALLY to the gallery (one component, no parallel copy). The owner-only
// sinks (Add to album, Archive, Load more) are simply the props we don't pass. Filtering is
// client-side here (the whole album is already loaded from the manifest).
//
// MediaItem mapping from the manifest: { url, key: `${slug}-${seq3}.${ext}`, label, size, uploaded }.
// The `key` gives downloads friendly names via `filenameFor` and drives `isClip` (Clips → a
// contact sheet); `label` is the per-album "Capture/Clip #seq" (from the manifest `seq`, so a
// digit-bearing title can't mislabel it); `size`/`uploaded` are the real values from the owner's
// media index — the card reads `size` (0 ⇒ its "Corrupt — unavailable" state) and shows `uploaded`
// as "Added" (the Shared album intentionally shows Added time — ADR-0059 §5).

import { useEffect, useMemo, useState } from "react";
import type { MediaItem, MediaCounts, MediaTypeFilter } from "../api";
import { isClip } from "../cores";
import type { BuildMosaicOptions, MosaicSelectionItem, SheetCores } from "../cores";
import { downloadItem } from "../download";
import { Loading } from "./Loading";
import { MediaGrid } from "./MediaGrid";

/**
 * One item in the public manifest — a monotonically-increasing display `seq`, a public CDN url,
 * and the real `size` (bytes) + `uploaded` (epoch ms "Added" time) from the owner's media index.
 */
interface PublicManifestItem {
  seq: number;
  url: string;
  size: number;
  uploaded: number;
}

/** `GET /a/<token>/manifest` — the album's public, credential-less manifest. */
interface PublicManifest {
  title: string;
  version: number;
  cover: string | null;
  count: number;
  items: PublicManifestItem[];
}

export interface PublicAlbumProps {
  /** The share token from /a/<token>[/<slug>] (already decoded by the App router). */
  token: string;
  /** Injectable per-item download (tests stub it); defaults to the real network flow. */
  onDownload?: (item: MediaItem) => Promise<void>;
  /** Injectable "download ZIP" flow (tests stub it); defaults to the real `downloadZip`. */
  onDownloadZip?: (items: MediaItem[]) => Promise<void>;
  /** Injectable Mosaic builder (tests stub it — WebCodecs is browser-only). */
  buildMosaic?: (
    items: MosaicSelectionItem[],
    options: BuildMosaicOptions,
  ) => Promise<Blob>;
  /** Injectable contact-Sheet decode/render seam (tests stub it); defaults to the real cores. */
  sheetCores?: SheetCores;
}

type Phase = "loading" | "notfound" | "error" | "ready";

export function PublicAlbum({
  token,
  onDownload,
  onDownloadZip,
  buildMosaic,
  sheetCores,
}: PublicAlbumProps): React.JSX.Element {
  const [phase, setPhase] = useState<Phase>("loading");
  const [manifest, setManifest] = useState<PublicManifest | null>(null);
  const [filter, setFilter] = useState<MediaTypeFilter>("all");

  // Fetch the PUBLIC manifest — a plain, same-origin, CREDENTIAL-LESS fetch (never the
  // Session-gated api client). 404 → the clean "not available" state; any other failure → error.
  useEffect(() => {
    let cancelled = false;
    setPhase("loading");
    setManifest(null);
    setFilter("all");
    fetch(`/a/${encodeURIComponent(token)}/manifest`, { credentials: "omit" })
      .then(async (res) => {
        if (cancelled) return;
        if (res.status === 404) {
          setPhase("notfound");
          return;
        }
        if (!res.ok) {
          setPhase("error");
          return;
        }
        const data = (await res.json()) as PublicManifest;
        if (cancelled) return;
        setManifest(data);
        setPhase("ready");
      })
      .catch(() => {
        if (!cancelled) setPhase("error");
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  // Build MediaItem-shaped objects from the manifest so <MediaGrid> + the tools work unchanged.
  const allItems = useMemo<MediaItem[]>(() => {
    if (!manifest) return [];
    const slug = slugify(manifest.title);
    return manifest.items.map((it) => {
      const isGif = ext(it.url) === "gif";
      return {
        url: it.url,
        key: `${slug}-${String(it.seq).padStart(3, "0")}.${ext(it.url)}`,
        // Label from the per-album manifest `seq`, NOT scraped from the key: a title slug with
        // digits (e.g. "summer-2024") would otherwise mislabel every card "Capture #2024".
        label: `${isGif ? "Clip" : "Capture"} #${it.seq}`,
        size: it.size,
        uploaded: it.uploaded,
      };
    });
  }, [manifest]);

  // Client-side filter (the whole album is already loaded — no server round-trip).
  const items = useMemo(
    () =>
      filter === "all"
        ? allItems
        : allItems.filter((it) => (filter === "clips") === isClip(it.key)),
    [allItems, filter],
  );

  // Whole-album per-type counts for the filter tabs (independent of the active filter).
  const counts = useMemo<MediaCounts>(() => {
    let photos = 0;
    let clips = 0;
    for (const it of allItems) {
      if (isClip(it.key)) clips++;
      else photos++;
    }
    return { all: allItems.length, photos, clips };
  }, [allItems]);

  if (phase === "loading") return <Loading label="Loading album…" />;

  if (phase === "notfound" || phase === "error") {
    return (
      <div className="public-album__unavailable" data-testid="public-album-unavailable" role="status">
        <p className="public-album__unavailable-title">
          {phase === "notfound" ? "This album isn’t available." : "Couldn’t load this album."}
        </p>
        <p>
          {phase === "notfound"
            ? "The link may be wrong, or the album is no longer shared."
            : "Something went wrong loading this album. Try again in a moment."}
        </p>
      </div>
    );
  }

  // ready
  const count = manifest!.count;

  return (
    <section className="public-album" data-testid="public-album">
      <header className="public-album__head">
        {/* The site logo, verbatim (.app__brand/.app__title) — identical look, links to the landing. */}
        <span className="app__brand">
          <a className="app__title" href="/" aria-label="Pixie — home">
            <b>PIX</b>IE
          </a>
        </span>
        {/* The owner-picked Album cover, small beside the title (owner call: a top hero was
            too prominent) — it used to drive only the OG unfurl, with no on-page payoff. */}
        <div className="public-album__titlerow">
          {manifest!.cover && (
            <img
              className="public-album__cover"
              src={manifest!.cover}
              alt=""
              crossOrigin="anonymous"
              data-testid="public-album-cover"
            />
          )}
          <h1 className="public-album__title" data-testid="public-album-title">
            {manifest!.title}
          </h1>
        </div>
        <p className="public-album__count" data-testid="public-album-count">
          {count} {count === 1 ? "item" : "items"}
        </p>
      </header>

      {/* A genuinely-empty share gets visitor copy — MediaGrid's empty branch is written for
          the owner's filtered gallery ("switch the filter above"), nonsense on a public page. */}
      {allItems.length === 0 ? (
        <p className="public-album__empty" data-testid="public-album-empty">
          This album is empty.
        </p>
      ) : (
        /* The gallery grid, read-only: Select (ZIP) + Mosaic (ordered pick) + Contact sheet +
           lightbox + per-item download — identical to the owner gallery. Owner-only sinks
           (Add to album / Archive / Load more) are the props we deliberately omit. */
        <MediaGrid
          items={items}
          hasMore={false}
          loadingMore={false}
          onLoadMore={noop}
          onDownload={onDownload ?? downloadItem}
          onDownloadZip={onDownloadZip}
          buildMosaic={buildMosaic}
          sheetCores={sheetCores}
          filter={filter}
          onFilter={setFilter}
          counts={counts}
          total={items.length}
          showMosaicNewest={false}
        />
      )}
    </section>
  );
}

function noop(): void {
  /* no pagination on the public viewer — the whole album is in the manifest */
}

/** The bmp/gif extension from a public CDN url (drives `isClip` + the download filename). */
function ext(url: string): "gif" | "bmp" {
  return /\.gif(?:$|[?#])/i.test(url) ? "gif" : "bmp";
}

/**
 * Slugify the album title for friendly download filenames. Mirrors AlbumDetail's `slugify`
 * (and the server's `canonicalSlug`): lowercase, non-alphanumerics → `-`, trimmed, capped 60,
 * fallback "album". Only cosmetic here — it prefixes the per-item download names.
 */
function slugify(name: string): string {
  const s = name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60);
  return s && s !== "manifest" ? s : "album";
}
